export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 32

/**
 * Caracteres especiais aceitos. Inclui os mais comuns (-, @, *, etc.)
 * sem tratar letras acentuadas como caractere especial.
 */
export const PASSWORD_SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/

export type PasswordChecklistKey =
  | 'length'
  | 'ascii'
  | 'noRepeats'
  | 'uniqueChar'
  | 'hasUppercase'
  | 'hasLowercase'
  | 'hasNumbers'
  | 'hasSpecialChars'
  | 'match'

type TranslateFn = (key: string, params?: Record<string, unknown>) => string

export const passwordValidators = {
  minMax: (value: string) => value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH,
  asciiOnly: (value: string) => /^[\x21-\x7E]+$/.test(value),
  noConsecutiveRepeat: (value: string) => !/(.)\1/.test(value),
  hasUniqueChar: (value: string) => {
    const counts = new Map<string, number>()

    for (const char of value) counts.set(char, (counts.get(char) || 0) + 1)

    return [...counts.values()].some((count) => count === 1)
  },
  hasUppercase: (value: string) => /[A-Z]/.test(value),
  hasLowercase: (value: string) => /[a-z]/.test(value),
  hasNumbers: (value: string) => /[0-9]/.test(value),
  hasSpecialChars: (value: string) => PASSWORD_SPECIAL_CHARS_REGEX.test(value)
}

export const createPasswordRules = (t: TranslateFn, getPassword: () => string) => ({
  password: [
    {
      name: 'required',
      test: (value: string) => value.length,
      message: () => t('validation.required')
    },
    {
      name: 'minMax',
      test: (value: string) => passwordValidators.minMax(value),
      message: () => t('newPassword.validation.minMax', { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })
    },
    {
      name: 'ascii',
      test: (value: string) => passwordValidators.asciiOnly(value),
      message: () => t('newPassword.validation.ascii')
    },
    {
      name: 'noRepeats',
      test: (value: string) => passwordValidators.noConsecutiveRepeat(value),
      message: () => t('newPassword.validation.noRepeats')
    },
    {
      name: 'uniqueChar',
      test: (value: string) => passwordValidators.hasUniqueChar(value),
      message: () => t('newPassword.validation.uniqueChar')
    },
    {
      name: 'uppercase',
      test: (value: string) => passwordValidators.hasUppercase(value),
      message: () => t('newPassword.validation.uppercase')
    },
    {
      name: 'lowercase',
      test: (value: string) => passwordValidators.hasLowercase(value),
      message: () => t('newPassword.validation.lowercase')
    },
    {
      name: 'numbers',
      test: (value: string) => passwordValidators.hasNumbers(value),
      message: () => t('newPassword.validation.numbers')
    },
    {
      name: 'specialChars',
      test: (value: string) => passwordValidators.hasSpecialChars(value),
      message: () => t('newPassword.validation.specialChars')
    }
  ],
  confirmPassword: [
    {
      name: 'required',
      test: (value: string) => value.length,
      message: () => t('placeholder.confirmPassword')
    },
    {
      name: 'match',
      test: (value: string) => !!value.length && value === getPassword(),
      message: () => t('validation.passwordMatch')
    }
  ]
})

export const passwordChecklist = (t: TranslateFn): [PasswordChecklistKey, string][] => [
  ['length', t('newPassword.rules.length', { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })],
  ['ascii', t('newPassword.rules.ascii')],
  ['noRepeats', t('newPassword.rules.noRepeats')],
  ['uniqueChar', t('newPassword.rules.hasUniqueChar')],
  ['hasUppercase', t('newPassword.rules.hasUppercase')],
  ['hasLowercase', t('newPassword.rules.hasLowercase')],
  ['hasNumbers', t('newPassword.rules.hasNumbers')],
  ['hasSpecialChars', t('newPassword.rules.hasSpecialChars')],
  ['match', t('newPassword.rules.match')]
]

const ruleToError: Record<PasswordChecklistKey, string> = {
  length: 'minMax',
  ascii: 'ascii',
  noRepeats: 'noRepeats',
  uniqueChar: 'uniqueChar',
  hasUppercase: 'uppercase',
  hasLowercase: 'lowercase',
  hasNumbers: 'numbers',
  hasSpecialChars: 'specialChars',
  match: 'match'
}

const formatErrors = (errors: any[] = []) =>
  (errors || []).reduce((acc, error) => {
    acc[error.name] = error.message
    return acc
  }, {} as Record<string, string>)

export const emptyPasswordChecklistState = () =>
  Object.keys(ruleToError).reduce((acc, key) => {
    acc[key] = ''
    return acc
  }, {} as Record<PasswordChecklistKey, string>)

/**
 * Monta o estado visual do checklist a partir do resultado do UiForm.
 */
export const getPasswordChecklistState = (formResult: any): Record<PasswordChecklistKey, string> => {
  if (!formResult?.$dirty) return emptyPasswordChecklistState()

  const passwordErrors = formatErrors(formResult.password?.$errors)
  const confirmPasswordErrors = formatErrors(formResult.confirmPassword?.$errors)

  return Object.entries(ruleToError).reduce((acc, [key, errorName]) => {
    const errors = key === 'match' ? confirmPasswordErrors : passwordErrors
    acc[key as PasswordChecklistKey] = errors[errorName] ? 'invalid' : 'valid'
    return acc
  }, {} as Record<PasswordChecklistKey, string>)
}
